import React, { useEffect } from 'react';
import { Body2, H5Style, H6Style, LinkButton } from '../../components/common';
import { H2 } from '../forms';
import PageContainer from '../PageContainer';
import PaddedContent from '../../components/housing/PaddedContent';
import { useMessageGetter } from 'react-message-context';
import { RouteComponentProps } from 'react-router';
import styled from 'styled-components/macro';
import { theme } from '../../theme';
import { DeviceSize } from '../../components/basic/DeviceSize';
import { ChevronDownIcon, TrailingIcon } from '../../assets';
import { formatDateTime } from '../../utils';
import FormStateWithIcon from './FormStateWithIcon';
import { EventFormState, EventFormType } from '../../types/events';
import { NoticeFormState, NoticeFormType } from '../../types/notices';
import { Link } from 'react-router-dom';
import { Routes } from '../../constants/routes';
import { getNoticeDisplayTitle } from '../my-building/notice-utils';
import { loadOwnForms } from '../../context/kennoTenantActions';
import { useKennoTenant } from '../../context/kennoTenantContext';
import { LOAD_STATES } from '../../context/common';
import Loader from '../../components/basic/Loader';
import { ExpenseReportFormState, ExpenseReportFormType } from '../../types/expense-reports';
import { getExpenseReportDisplayTitle } from '../forms/expense-report-utils';
import { FormState, Form } from '../../types/forms';

const FormsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 60px;

  th,
  td {
    text-align: left;
  }

  p {
    margin: 20px 0 0 12px;
  }
`;

const LabelRow = styled.tr`
  border-bottom: 2px solid ${theme().colors.coal};

  svg {
    width: 12px;
    height: 12px;
    margin-left: 10px;
  }

  @media ${DeviceSize.desktopS} {
    .name-label,
    .state-label {
      display: none;
    }
  }
`;

const Label = styled.th`
  ${H6Style}
  padding: 12px;

  @media ${DeviceSize.desktopS} {
    padding-bottom: 20px;
  }
`;

const TableRow = styled.tr`
  position: relative;
  border-bottom: 1px solid ${theme().colors.m2Black20};

  @media ${DeviceSize.desktopS} {
    display: flex;
    flex-direction: column;
    padding: 8px 0;
  }
`;

const TableData = styled.td`
  padding: 12px;

  @media ${DeviceSize.desktopS} {
    border-bottom: none;
  }
`;

const NameLink = styled(Link)`
  ${H5Style}
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 430px;
  cursor: pointer;
`;

const Name = styled(TableData)`
  div {
    display: inline-flex;
  }
  svg {
    margin-right: 20px;
    width: 24px;
    height: 24px;

    @media ${DeviceSize.desktopS} {
      display: none;
    }
  }

  @media ${DeviceSize.desktopS} {
    max-width: 250px;
    padding-bottom: 2px;
  }
`;

const ModifiedAt = styled(TableData)`
  @media ${DeviceSize.desktopS} {
    display: none;
  }
`;

const TableButtons = styled(TableData)`
  display: flex;
  justify-content: flex-end;
  gap: 8px;

  @media ${DeviceSize.desktopS} {
    position: absolute;
    top: 0;
    right: 0;
  }
`;

const ModifyButton = styled(LinkButton)`
  @media ${DeviceSize.desktopM} {
    width: 40px;
    padding: 8px;
    span {
      display: none;
    }
  }
  @media ${DeviceSize.mobileL} {
    width: 32px !important;
    padding: 6px !important;
  }
`;

/* const DeleteButton = styled(Button)`
  width: 40px;
  padding: 8px;
`; */

const OwnFormsPage = ({ history }: RouteComponentProps) => {
  const t = useMessageGetter('ownForms');
  const tForms = useMessageGetter('forms');
  const [{ ownForms }, dispatch] = useKennoTenant();

  useEffect(() => {
    loadOwnForms(dispatch);
  }, [dispatch]);

  const loading = ownForms.loadState === LOAD_STATES.BUSY;

  const forms = ownForms.data;
  const hasForms = forms?.length;

  const allowEditAndDelete = (state: FormState) =>
    state === EventFormState.Draft ||
    state === EventFormState.Rejected ||
    state === NoticeFormState.Draft ||
    state === NoticeFormState.Rejected ||
    state === ExpenseReportFormState.Draft ||
    state === ExpenseReportFormState.Rejected;

  const getDisplayName = (form: Form) => {
    switch (form.formType) {
      case EventFormType.EventInvite:
        return form.formData.name;
      case NoticeFormType.ActionPlan:
      case NoticeFormType.ActionReport:
      case NoticeFormType.MeetingMinutes:
        return getNoticeDisplayTitle(form, tForms);
      case ExpenseReportFormType.GeneralExpenseReport:
      case ExpenseReportFormType.TravelExpenseReport:
        return getExpenseReportDisplayTitle(form, tForms);
    }
  };

  return (
    <PageContainer history={history}>
      <PaddedContent>
        <H2>{t('title')}</H2>
        <Body2 margin={'30px 0'}>{t('description')}</Body2>
        <FormsTable>
          <thead>
            <LabelRow>
              <Label className="name-label">{t('table.name')}</Label>
              <Label className="state-label">{t('table.state')}</Label>
              <Label>
                {t('table.updated')}
                <ChevronDownIcon />
              </Label>
            </LabelRow>
          </thead>
          <tbody>
            {loading ? (
              <Loader />
            ) : hasForms ? (
              forms.map((form) => (
                <TableRow key={form.id}>
                  <Name>
                    <div>
                      <TrailingIcon />
                      <NameLink to={`${Routes.OwnForms.ALL_FORMS}/${form.id}`}>{getDisplayName(form)}</NameLink>
                    </div>
                  </Name>
                  <TableData>
                    <FormStateWithIcon state={form.formState} />
                  </TableData>
                  <ModifiedAt>{formatDateTime(form.modifiedAt)}</ModifiedAt>
                  <TableButtons>
                    {allowEditAndDelete(form.formState) && (
                      <ModifyButton
                        to={`${Routes.OwnForms.ALL_FORMS}/muokkaa/${form.id}`}
                        size="medium"
                        icon="edit"
                        primary
                      >
                        <span>{t('update')}</span>
                      </ModifyButton>
                    )}
                    {/* Awaiting implementation of the delete endpoint. */}
                    {/* <DeleteButton 
                    icon="thrashFilled"
                    size="medium" 
                    disabled={!allowEditAndDelete(form.formState)}
                    /> */}
                  </TableButtons>
                </TableRow>
              ))
            ) : (
              <Body2>{t('noForms')}</Body2>
            )}
          </tbody>
        </FormsTable>
      </PaddedContent>
    </PageContainer>
  );
};

export default OwnFormsPage;
